import {
  Component,
  Input,
  Output,
  EventEmitter,
  Inject,
  PLATFORM_ID,
  inject,
  signal,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MessageModule } from 'primeng/message';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ScreenSizeService } from '@Shared/services';

@Component({
  selector: 'app-embedded-code-dialog',
  templateUrl: './embedded-code-dialog.component.html',
  styleUrls: ['./embedded-code-dialog.component.scss'],
  standalone: true,
  imports: [
    DialogModule,
    SelectButtonModule,
    FormsModule,
    NgClass,
    MessageModule
  ]
})
export class EmbeddedCodeDialogComponent implements OnChanges {
  showEmbeded = signal(true);
  public safeUrl: SafeResourceUrl | null = null;
  public isMobile;

  private screenSizeService = inject(ScreenSizeService);
  private sanitizer = inject(DomSanitizer);

  private _alias: string = '';
  @Input()
  set alias(value: string) {
    this._alias = value;
    this.updateSafeUrl();
  }
  get alias(): string {
    return this._alias;
  }

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  embededSize = 'Small';
  cardSizes = ['Small', 'Medium', 'Large', 'Responsive (Advanced)'];

  constructor(@Inject(PLATFORM_ID) private platformId: any) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['alias']) {
      this.updateSafeUrl();
    }

    this.isMobile = this.screenSizeService.isMobile;
  }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private get origin(): string {
    return this.isBrowser ? window.location.origin : '';
  }

  private updateSafeUrl(): void {
    if (!this.isBrowser || !this._alias) return;
    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `${this.origin}/embedded-card/${this._alias}`
    );
  }

  get embeddedUrl(): string {
    return `${this.origin}/embedded-card/${this.alias}`;
  }

  get loaderScriptUrl(): string {
    if (!this.isBrowser) return '';
    return `<script src="${this.origin}/assets/scripts/embed-loader.js"></script>`;
  }

  get iframeEmbedCode(): string {
    return [
      `<iframe ${this.embededSize === 'Responsive (Advanced)' ? 'style="position: absolute;"' : ''}`,
      `  src="${this.embeddedUrl}"`,
      `  data-pitchcard-embed="true"`,
      `  allow="autoplay; fullscreen; picture-in-picture; encrypted-media"`,
      `  height="${
        this.embededSize === 'Small'
          ? '400px'
          : this.embededSize === 'Medium'
            ? '450px'
            : this.embededSize === 'Large'
              ? '500px'
              : '100%'
      }"`,
      `  frameborder="0" allowfullscreen>`,
      `</iframe>`
    ].join('\n');
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  copyEmbedText(text: string, btnEl: HTMLButtonElement): void {
    if (!this.isBrowser || !text) return;
    this.copyText(text).then((isCopied) => {
      if (isCopied) {
        const original = btnEl.textContent;
        btnEl.textContent = 'Copied!';
        setTimeout(() => (btnEl.textContent = original), 1500);
      }
    });
  }

  versionChange(item) {
    this.showEmbeded.set(false);
    setTimeout(() => {
      this.showEmbeded.set(true); // Reload embded displaying
    });
  }

  private async copyText(text: string): Promise<boolean> {
    if (!this.isBrowser || !text) return false;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // fall through to legacy approach
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
